import './item-styles.css.js';
import '@magnetic/theme';
import { Flex } from '@magnetic/flex';
import { useClassNames } from '@magnetic/hooks';
import { Tag } from '@magnetic/tag';
import React, { forwardRef, useMemo, cloneElement, useCallback } from 'react';
import { useNavContext } from '../nav-context.js';
import { NavItemCaret } from './caret.js';
import { NavItemContent } from './content.js';
import { NavItemIcon } from './icon.js';
import { NavItemTooltip } from './tooltip.js';

var __defProp = Object.defineProperty;
var __name = (target, value) => __defProp(target, "name", { value, configurable: true });
const computeFlexGap = /* @__PURE__ */ __name((isSwitcher, isCollapsed, isIconOnly) => {
  const expanded = !isCollapsed;
  const collapsed = isCollapsed && !isIconOnly;
  const collapsedIcon = isCollapsed && isIconOnly;
  let gap = 0;
  if (expanded && isSwitcher) {
    gap = 12;
  }
  if (expanded && !isSwitcher) {
    gap = 16;
  }
  if (collapsed) {
    gap = 4;
  }
  if (collapsedIcon) {
    gap = 0;
  }
  return gap;
}, "computeFlexGap");
const useWrappedClick = /* @__PURE__ */ __name(({
  drawerContentId,
  label,
  onClick
}) => {
  const { closeDrawer, openId, setOpenId, setOpenLabel, setSelectedItemId } = useNavContext();
  return useCallback(
    (event) => {
      onClick == null ? void 0 : onClick(event);
      if (!drawerContentId || drawerContentId === openId) {
        closeDrawer == null ? void 0 : closeDrawer();
      } else {
        setOpenId == null ? void 0 : setOpenId(drawerContentId);
        setOpenLabel == null ? void 0 : setOpenLabel(label);
        setSelectedItemId == null ? void 0 : setSelectedItemId(drawerContentId);
      }
    },
    [
      closeDrawer,
      drawerContentId,
      label,
      onClick,
      openId,
      setOpenId,
      setOpenLabel,
      setSelectedItemId
    ]
  );
}, "useWrappedClick");
const useIsOpen = /* @__PURE__ */ __name((drawerContentId) => {
  const { selectedItemId } = useNavContext();
  return selectedItemId !== void 0 && selectedItemId === drawerContentId;
}, "useIsOpen");
const RenderNavItem = /* @__PURE__ */ __name(({
  as,
  className,
  drawerContentId,
  heading,
  icon,
  isNew,
  isSelected = false,
  label,
  onClick,
  switcher,
  ...rest
}, ref) => {
  const { isCollapsed, isDrawerOpen, isIconOnly, openId } = useNavContext();
  const isOpen = useIsOpen(drawerContentId);
  const Component = useMemo(() => as != null ? as : "button", [as]);
  const classNames = useClassNames(
    "mds-rebuild-nav-item",
    {
      "mds-rebuild-nav-item-switcher": switcher,
      "mds-rebuild-nav-item-is-collapsed": isCollapsed,
      "mds-rebuild-nav-item-is-icon-only": isIconOnly,
      "mds-rebuild-nav-item-is-selected": isSelected,
      "mds-rebuild-nav-item-is-open": isOpen
    },
    className
  );
  const switcherClassNames = useClassNames("mds-rebuild-nav-item", {
    "mds-rebuild-nav-item-switcher": switcher,
    "mds-rebuild-nav-item-switcher-is-collapsed": isCollapsed,
    "mds-rebuild-nav-item-switcher-is-icon-only": isIconOnly,
    "mds-rebuild-nav-item-is-selected": isSelected,
    "mds-rebuild-nav-item-switcher-is-open": isOpen
  });
  const navItemContentClassnames = useClassNames({
    "mds-rebuild-nav-item-content": !isCollapsed,
    "mds-rebuild-nav-item-content-is-collapsed": isCollapsed
  });
  const wrappedClick = useWrappedClick({ drawerContentId, onClick, label });
  const isThisDrawerOpen = isDrawerOpen && openId === drawerContentId;
  const gap = computeFlexGap(switcher, isCollapsed, isIconOnly);
  return /* @__PURE__ */ React.createElement(NavItemTooltip, {
    enabled: isIconOnly && isCollapsed && !isThisDrawerOpen,
    label
  }, /* @__PURE__ */ React.createElement(Component, {
    className: switcher ? switcherClassNames : classNames,
    ref,
    ...rest,
    "aria-expanded": isOpen,
    onClick: wrappedClick
  }, /* @__PURE__ */ React.createElement(Flex, {
    align: "center",
    direction: isCollapsed ? "vertical" : "horizontal",
    gap
  }, /* @__PURE__ */ React.createElement(NavItemIcon, {
    icon: icon && cloneElement(icon, {
      active: isSelected || isOpen,
      collapsed: isCollapsed,
      width: switcher ? 32 : 24,
      height: switcher ? 32 : 24,
      viewBox: "0 0 24 24"
    })
  }), /* @__PURE__ */ React.createElement(Flex, {
    className: navItemContentClassnames,
    gap: 8
  }, /* @__PURE__ */ React.createElement(NavItemContent, {
    heading,
    isActive: isSelected || isOpen,
    isSwitcher: switcher,
    label
  }), isNew && !isCollapsed && /* @__PURE__ */ React.createElement(Tag.Colored, {
    className: "mds-rebuild-nav-item-tag",
    color: 1,
    size: "sm"
  }, "New"))), drawerContentId && /* @__PURE__ */ React.createElement(NavItemCaret, {
    isOpen
  })));
}, "RenderNavItem");
const NavItem = forwardRef(RenderNavItem);
NavItem.displayName = "Nav.Item";

export { NavItem };
//# sourceMappingURL=index.js.map
